import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Paper,
  Tooltip,
  Box,
  Chip,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import * as actions from "../Releases/actions";
import { Link2, User, Mail, Music, Disc, Calendar, Facebook, Instagram, ExternalLink, Copy, ClipboardCheck } from 'lucide-react';
import _ from "lodash";
import { toastError, toastSuccess, copyToClipboard } from "../../utils";
import CustomShowModal from "../Common/customShowModal";
import SearchInput from '../../components/SearchInput';
import StatusBadge from '../../components/StatusBadge';
import EmptyState from '../../components/EmptyState';
import ActionButton from '../../components/ActionButton';

function CopyMeta({ icon, label, value }) {
  const [copied, setCopied] = useState(false);
  if (!value || value === '-') {
    return (
      <Tooltip title={label} arrow placement="top">
        <span className="approved-release-card__meta-item">
          {icon} -
        </span>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={copied ? "Copied!" : `${label} — click to copy`} arrow placement="top">
      <span
        className="approved-release-card__meta-item approved-release-card__meta-item--copyable"
        onClick={() => { copyToClipboard(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      >
        {icon} {value}
        {copied ? <ClipboardCheck size={11} className="copy-tick" /> : <Copy size={11} className="copy-icon" />}
      </span>
    </Tooltip>
  );
}

export default function Linking(props) {
  const [PLinkNew, setPLinkNew] = useState([]);
  const [PLinkOld, setPLinkOld] = useState([]);
  const [actionsModal, setActionsModal] = useState(false);
  const [actionId, setActionId] = useState();
  const [count, setCount] = useState(0);

  const [filters, setFilters] = useState({
    limit: 50,
    offset: 0,
    page: 1,
    search: "",
  });

  const delaySearch = useCallback(
    _.debounce((filters) => onGetPlinkOld(filters), 500),
    []
  );

  useEffect(() => {
    document.title = 'PL | Profile Linking';
    return () => { document.title = 'Fronic Media Admin'; };
  }, []);

  useEffect(() => {
    onGetPlinkOld(filters);
    setTimeout(() => {
      props.setLoader(false);
    }, 5000);
  }, []);

  const onGetPlinkOld = (filters) => {
    props.setLoader(true);
    actions
      .getProfileLinkingOldAdmin({ ...filters })
      .then((res) => {
        props.setLoader(false);
        setPLinkOld(res.body);
        setCount(res.total);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Operation failed');
      });
  };

  const onPageChange = (event, page) => {
    const offset = (page - 1) * filters.limit;
    const newFilters = { ...filters, page, offset };
    setFilters(newFilters);
    onGetPlinkOld(newFilters);
  };

  const onChangeSearch = (e) => {
    const newFilters = { ...filters, search: e.target.value };
    setFilters(newFilters);
    delaySearch(newFilters);
  };

  const onGetPLinkNew = () => {
    props.setLoader(true);
    actions
      .getProfileLinkingRequestAdmin()
      .then((res) => {
        setPLinkNew(res.body);
        props.setLoader(false);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Failed to load profile linking requests');
      });
  };

  useEffect(() => {
    onGetPLinkNew();
  }, []);

  let totalPages = Math.ceil(Number(count) / filters.limit);

  const handleActionOnClick = (item) => {
    setActionsModal(true);
    setActionId(item);
  };

  const onConfirmAcceptModal = (status) => {
    props.setLoader(true);
    actions
      .updateProfileLinkingstatusAdmin({ status: status, plink_id: actionId })
      .then((res) => {
        setActionsModal(false);
        onGetPLinkNew();
        onGetPlinkOld(filters);
        toastSuccess(res.message || 'Profile linking status updated successfully');
        props.setLoader(false);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Failed to update profile linking status');
      });
  };

  const renderCard = (item, isNew) => (
    <Paper
      key={item.plink_id}
      className="approved-release-card"
      style={{ cursor: 'default' }}
    >
      <div className="approved-release-card__artwork">
        <div className="approved-release-card__artwork-fallback">
          <Link2 size={20} color="#64748B" />
          <span>Link</span>
        </div>
      </div>

      <div className="approved-release-card__body">
        <div className="approved-release-card__top">
          <div className="approved-release-card__title-row">
            <Typography className="approved-release-card__title">
              {item.release_title || 'Untitled'}
            </Typography>
          </div>
          <div className="approved-release-card__status" style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <Box sx={{ minWidth: 80, display: 'flex', justifyContent: 'center' }}>
              {isNew ? (
                <Box display="flex" gap={0.75}>
                  <ActionButton variant="success" sx={{ py: 0.2, px: 1.5, fontSize: '0.75rem' }} onClick={() => handleActionOnClick(item.plink_id)}>
                    Accept
                  </ActionButton>
                  <ActionButton variant="danger" sx={{ py: 0.2, px: 1.5, fontSize: '0.75rem' }} onClick={() => handleActionOnClick(item.plink_id)}>
                    Reject
                  </ActionButton>
                </Box>
              ) : (
                <StatusBadge status={item.status} />
              )}
            </Box>
          </div>
        </div>

        <div className="approved-release-card__meta">
          <span className="approved-release-card__id">FM-PL-{item.plink_id}</span>
          <span className="approved-release-card__id">RL-{item.release_id}</span>
          <CopyMeta icon={<Music size={13} />} label="Audio" value={item.audio_title} />
          <CopyMeta icon={<Disc size={13} />} label="Artist" value={item.artist} />
        </div>
        <div className="approved-release-card__meta" style={{ marginTop: 6 }}>
          <CopyMeta icon={<User size={13} />} label="User" value={item.name} />
          <CopyMeta icon={<Mail size={13} />} label="Email" value={item.email} />
          {item.date && (
            <CopyMeta icon={<Calendar size={13} />} label="Date" value={item.date} />
          )}
          {item.facebook_link && (
            <CopyableSocialLink icon={<Facebook size={13} />} label="Facebook" value={item.facebook_link} />
          )}
          {item.instagram_link && (
            <CopyableSocialLink icon={<Instagram size={13} />} label="Instagram" value={item.instagram_link} />
          )}
        </div>
      </div>
    </Paper>
  );

  return (
    <Grid container>
      {/* ========== Review Required ========== */}
      <Grid item xs={12}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
          <Typography variant="h6" sx={{ color: '#F1F5F9', fontWeight: 600, fontSize: '1rem' }}>
            Review Required
          </Typography>
          <Chip
            icon={<Link2 size={14} />}
            label={`${PLinkNew.length} Pending`}
            sx={{
              backgroundColor: 'rgba(245, 158, 11, 0.12)',
              color: '#F59E0B',
              border: '1px solid rgba(245, 158, 11, 0.25)',
              fontWeight: 600,
              fontSize: '0.85rem',
              height: 34,
              flexShrink: 0,
              '& .MuiChip-icon': { color: '#F59E0B' },
            }}
          />
        </Box>
        <Box className="approved-release-list" mb={4}>
          {PLinkNew.length ? (
            PLinkNew.map((item) => renderCard(item, true))
          ) : (
            <Paper sx={{ p: 0 }}>
              <table style={{ width: '100%' }}><tbody>
                <EmptyState message="No Requests To Review" colSpan={1} />
              </tbody></table>
            </Paper>
          )}
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ borderColor: 'var(--border)', mb: 3 }} />
      </Grid>

      {/* ========== History ========== */}
      <Grid item xs={12}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
          <Typography variant="h6" sx={{ color: '#F1F5F9', fontWeight: 600, fontSize: '1rem' }}>
            Profile Linking History
          </Typography>
          <Chip
            icon={<Link2 size={14} />}
            label={`${count} Total`}
            sx={{
              backgroundColor: 'rgba(108, 99, 255, 0.12)',
              color: '#6C63FF',
              border: '1px solid rgba(108, 99, 255, 0.25)',
              fontWeight: 600,
              fontSize: '0.85rem',
              height: 34,
              flexShrink: 0,
              '& .MuiChip-icon': { color: '#6C63FF' },
            }}
          />
        </Box>
        <Box mb={3} sx={{ maxWidth: 420 }}>
          <SearchInput
            onChange={onChangeSearch}
            value={filters.search}
            placeholder="Search by name, email, or release..."
          />
        </Box>
        <Box className="approved-release-list">
          {PLinkOld.length ? (
            PLinkOld.map((item) => renderCard(item, false))
          ) : (
            <Paper sx={{ p: 0 }}>
              <table style={{ width: '100%' }}><tbody>
                <EmptyState message="No Profile Linking History Found" colSpan={1} />
              </tbody></table>
            </Paper>
          )}
        </Box>

        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={totalPages}
              showFirstButton
              showLastButton
              onChange={onPageChange}
              page={filters.page || 1}
              sx={{
                '& .MuiPaginationItem-root': {
                  color: '#94A3B8',
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(108, 99, 255, 0.2)',
                    color: '#6C63FF',
                  },
                },
              }}
            />
          </Box>
        )}
      </Grid>

      {/* ========== Action Modal ========== */}
      <CustomShowModal
        handleClose={() => setActionsModal(false)}
        title="What's your action?"
        open={actionsModal}
        maxWidth="sm"
      >
        <Box display="flex" justifyContent="center" gap={2} py={2}>
          <ActionButton variant="success" onClick={() => onConfirmAcceptModal("approved")}>
            Accept
          </ActionButton>
          <ActionButton variant="danger" onClick={() => onConfirmAcceptModal("rejected")}>
            Reject
          </ActionButton>
        </Box>
      </CustomShowModal>
    </Grid>
  );
}

function CopyableSocialLink({ icon, label, value }) {
  const [copied, setCopied] = useState(false);
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        backgroundColor: 'rgba(108, 99, 255, 0.08)',
        border: '1px solid rgba(108, 99, 255, 0.15)',
        borderRadius: 4,
        padding: '3px 10px',
        marginLeft: 4,
      }}
    >
      {icon}
      <span style={{ color: '#C4B5FD', fontSize: '0.78rem', fontWeight: 500 }}>
        {label}
      </span>
      <Tooltip title={copied ? "Copied!" : "Copy link"} arrow placement="top">
        <IconButton
          size="small"
          onClick={() => { copyToClipboard(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
          sx={{ color: copied ? '#10B981' : 'var(--text-secondary)', '&:hover': { color: 'var(--primary)' }, p: 0.3 }}
        >
          {copied ? <ClipboardCheck size={12} /> : <Copy size={12} />}
        </IconButton>
      </Tooltip>
      <Tooltip title="Open in new tab" arrow placement="top">
        <IconButton
          size="small"
          component="a"
          href={value}
          target="_blank"
          rel="noreferrer"
          sx={{ color: 'var(--text-secondary)', '&:hover': { color: 'var(--primary)' }, p: 0.3 }}
        >
          <ExternalLink size={12} />
        </IconButton>
      </Tooltip>
    </span>
  );
}
